<template>
  <div class="relative inline-block text-left"
      @keydown.up.stop.prevent="focusPrev"
      @keydown.down.stop.prevent="focusNext"
      @keydown.esc.stop.prevent="close"
      @focusout="(event) => focusOut(event)"
      v-on-clickaway="close"
      >
    <div @keydown.enter.stop.prevent="toggle">
      <slot
        name="trigger"
        v-bind="{isOpen: _isOpen, ...triggerProps}"
      />
    </div>
    <slot
      v-if="_isOpen"
      v-bind="{isOpen: _isOpen, close}"
    />
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue3-click-away';
import Button from '../button/Button.vue';

export default {
  components: {
    [Button.name]: Button,
  },
  name: 'i-dropdown',
  directives: {
    onClickaway,
  },
  provide() {
    return {
      $dropdownContext: () => ({
        activeIndex: this.activeIndex,
        close: this.close,
      }),
    };
  },
  data() {
    return {
      activeIndex: -1,
      uncontrolledIsOpen: false,
    };
  },
  props: {
    isOpen: {
      type: Boolean,
      default: null,
    },
  },
  methods: {
    open() {
      if (this._isOpen) return;
      this._isOpen = true;
    },
    close() {
      if (!this._isOpen) return;
      this._isOpen = false;
    },
    toggle() {
      this._isOpen = !this._isOpen;
    },
    focusPrev() {
      this.activeIndex = Math.max(0, this.activeIndex - 1);
    },
    focusNext() {
      this.activeIndex += 1;
    },
    focusOut(event) {
      // Close dropdown only if we tab out of the element and all its descendants
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.close();
      }
    },
  },
  computed: {
    triggerProps() {
      return {
        attrs: {
          'aria-haspopup': 'true',
          'aria-expanded': Boolean(this._isOpen).toString(),
          tabindex: 0,
        },
        toggle: this.toggle,
      };
    },
    /**
     * 1.) `controlled` (i.e. the consumer passes the `isOpen` prop)
     * 2.) `uncontrolled` (i.e. the dropdown manages its own local open/close state)
     */
    isControlled() {
      return typeof this.isOpen === 'boolean';
    },
    /**
     * update the open state
     * based on the current 'controlled' or 'uncontrolled' behavior
     */
    _isOpen: {
      get() {
        return this.isControlled ? this.isOpen : this.uncontrolledIsOpen;
      },
      set(value) {
        if (this.isControlled) {
          this.$emit('update:isOpen', value);
        } else {
          this.uncontrolledIsOpen = value;
        }
      },
    },
  },
  watch: {
    _isOpen: {
      immediate: true,
      handler(value) {
        // Note: emit open / close event on next tick
        // to ensure that the children components are mounted
        // since we are using v-if to render them
        this.$nextTick(() => {
          this.$emit(value ? 'open' : 'close', value);
        });
      },
    },
  },
};

</script>

<style>
  .relative {
    position: relative;
  }
</style>
